import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Cell,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { fetchClientAnalytics } from "../../redux/Slices/dashboardSlice";
import "../../styles/_client_analysis.scss";
import Loader from "../../components/Common/Loader";
import { normalizeDateForAPI } from "../../utils/Utils";
import appInsights from "../../AppInsights";

function Chart({ startDate, endDate }) {
  const dispatch = useDispatch();

  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;

  const { clientAnalytics, loading } = useSelector((state) => state.dashboard);

  // Track chart component load
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackEvent({
      name: "Chart_Component_Loaded",
      properties: {
        openedAt: openTime,
        coachId: coachId,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        component: "Client_Overview_Chart",
      },
    });
  }, []);

  useEffect(() => {
    if (coachId) {
      // Track analytics fetch attempt
      appInsights.trackEvent({
        name: "Client_Analytics_Fetch_Attempt",
        properties: {
          coachId: coachId,
          startDate: startDate ? normalizeDateForAPI(startDate) : null,
          endDate: endDate ? normalizeDateForAPI(endDate) : null,
          dateRangeProvided: !!(startDate && endDate),
        },
      });

      dispatch(
        fetchClientAnalytics({
          coachId,
          startDate: startDate ? normalizeDateForAPI(startDate) : null,
          endDate: endDate ? normalizeDateForAPI(endDate) : null,
        })
      );
    }
  }, [dispatch, startDate, endDate, coachId]);

  // Track successful analytics load
  useEffect(() => {
    if (!loading && clientAnalytics && coachId) {
      appInsights.trackEvent({
        name: "Client_Analytics_Loaded",
        properties: {
          coachId: coachId,
          totalClients: clientAnalytics.totalClients,
          activeClients: clientAnalytics.activeClients,
          startingClients: clientAnalytics.startingClients,
          endingClients: clientAnalytics.endingClients,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          chartDataPoints: chartData.length,
        },
      });
    }
  }, [loading, clientAnalytics, coachId, startDate, endDate]);

  // Track date range changes
  useEffect(() => {
    if (coachId && (startDate || endDate)) {
      appInsights.trackEvent({
        name: "Chart_Date_Range_Changed",
        properties: {
          coachId: coachId,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          hasStartDate: !!startDate,
          hasEndDate: !!endDate,
        },
      });
    }
  }, [startDate, endDate, coachId]);

  const chartData = clientAnalytics
    ? [
        { name: "Active", value: clientAnalytics.activeClients },
        { name: "Starting", value: clientAnalytics.startingClients },
        { name: "Ending", value: clientAnalytics.endingClients },
      ]
    : [];

  const colors = ["#383D8D", "#36C1A8", "#A42066"];

  // Track chart rendering
  useEffect(() => {
    if (chartData.length > 0 && !loading) {
      appInsights.trackEvent({
        name: "Chart_Rendered",
        properties: {
          coachId: coachId,
          dataPoints: chartData.length,
          totalValue: chartData.reduce((sum, item) => sum + item.value, 0),
          hasData: chartData.some(item => item.value > 0),
        },
      });
    }
  }, [chartData, loading, coachId]);

  // FIXED: Track chart interaction (hover/click)
  const handleChartInteraction = (data, index, event) => {
    try {
      // Recharts event handlers can receive parameters in different orders
      // Let's safely extract the information we need
      let interactionData, interactionType, barData;
      
      // Determine parameter order based on what's provided
      if (typeof data === 'object' && data !== null) {
        // First parameter is likely the data object
        interactionData = data;
        interactionType = event?.type || 'hover'; // Default to hover if event is undefined
      } else {
        // Parameters might be in different order, use safe defaults
        interactionData = {};
        interactionType = 'unknown';
      }
      
      // Safely extract the active payload
      const activePayload = interactionData.activePayload?.[0];
      if (activePayload?.payload) {
        barData = activePayload.payload;
        
        appInsights.trackEvent({
          name: "Chart_Data_Point_Interacted",
          properties: {
            coachId: coachId,
            dataPoint: barData.name,
            value: barData.value,
            interactionType: interactionType,
            chartType: "vertical_bar",
            payloadAvailable: true,
          },
        });
      } else {
        // Track when we have interaction but no payload data
        appInsights.trackException({
          exception: new Error("Chart_Interaction_No_Payload"),
          severityLevel: 1,
          properties: {
            coachId: coachId,
            interactionType: interactionType,
            hasActiveLabel: !!interactionData.activeLabel,
            activeLabel: interactionData.activeLabel,
          },
        });
      }
    } catch (error) {
      console.error('Error in handleChartInteraction:', error);
      // Track the error
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          coachId: coachId,
          error: error.message,
          component: "handleChartInteraction",
        },
      });
    }
  };

  // Alternative approach: Separate handlers for click and hover
  const handleBarClick = (data, index, event) => {
    try {
      const payload = data?.payload || data?.activePayload?.[0]?.payload;
      if (payload) {
        appInsights.trackEvent({
          name: "Chart_Bar_Clicked",
          properties: {
            coachId: coachId,
            dataPoint: payload.name,
            value: payload.value,
            chartType: "vertical_bar",
          },
        });
      }
    } catch (error) {
      console.error('Error in handleBarClick:', error);
    }
  };

  const handleBarHover = (data, index, event) => {
    try {
      const payload = data?.payload || data?.activePayload?.[0]?.payload;
      if (payload) {
        appInsights.trackEvent({
          name: "Chart_Bar_Hovered",
          properties: {
            coachId: coachId,
            dataPoint: payload.name,
            value: payload.value,
            chartType: "vertical_bar",
          },
        });
      }
    } catch (error) {
      console.error('Error in handleBarHover:', error);
    }
  };

  if (loading) {
    // Track loading state
    appInsights.trackEvent({
      name: "Chart_Loading_State",
      properties: {
        coachId: coachId,
        component: "Client_Overview_Chart",
      },
    });
    return <Loader />;
  }

  if (!clientAnalytics) {
    // Track no data scenario
    appInsights.trackEvent({
      name: "Chart_No_Data_Available",
      properties: {
        coachId: coachId,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        reason: "clientAnalytics_null",
      },
    });
    return <div>No data available</div>;
  }

  // Track empty data scenario
  if (chartData.length === 0 || chartData.every(item => item.value === 0)) {
    appInsights.trackEvent({
      name: "Chart_Empty_Data",
      properties: {
        coachId: coachId,
        totalClients: clientAnalytics.totalClients,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        reason: "all_zero_values",
      },
    });
  }

  return (
    <div className="card-client ">
      <div className="total-clients-container-client">
        <div className="website-hits-header ">
          {/* Title Section */}
          <div className="website-hits-title">
            <h1> Client Overview</h1>
          </div>

          {/* Total Hits Section */}
          <div className="website-hits-total">
            <h1>Total Clients</h1>
            <p className="total-hits-count">{clientAnalytics.totalClients}</p>
          </div>
        </div>

        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              layout="vertical"
              onClick={handleBarClick} // Use separate click handler
            >
              <XAxis
                type="number"
                label={{
                  value: "No of Clients",
                  position: "bottom",
                  offset: -4,
                }}
              />
              <YAxis dataKey="name" type="category" />

              <Bar 
                dataKey="value" 
                fill="#8884d8" 
                radius={[0, 12, 12, 0]}
                onMouseEnter={handleBarHover} // Use separate hover handler
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
                <LabelList
                  dataKey="value"
                  position="insideRight"
                  fill="#FFFFFF"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default Chart;